import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import healthService from '../../services/healthService';
import studentsService from '../../services/studentsService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash } from '@phosphor-icons/react';

const emptyForm = {
  student_id: '',
  blood_group: '',
  allergies: '',
  chronic_conditions: '',
  emergency_medical_contact_name: '',
  emergency_medical_contact_phone: '',
  notes: '',
};

function HealthProfilesPage() {
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredProfiles(profiles);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredProfiles(profiles.filter((p) => getStudentName(p.student_id).toLowerCase().includes(term)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, searchTerm, students]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [profilesData, studentsData] = await Promise.all([
        healthService.listProfiles(),
        studentsService.listStudents().catch(() => []),
      ]);
      setProfiles(Array.isArray(profilesData) ? profilesData : []);
      setStudents(Array.isArray(studentsData) ? studentsData : studentsData?.items || studentsData?.students || []);
    } catch (error) {
      toast.error('Failed to load health profiles');
    } finally {
      setLoading(false);
    }
  };

  const getStudentName = (studentId) => {
    const student = students.find((s) => s.student_id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : studentId;
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (profile) => {
    setEditingId(profile.id);
    setFormData({
      student_id: profile.student_id,
      blood_group: profile.blood_group || '',
      allergies: profile.allergies || '',
      chronic_conditions: profile.chronic_conditions || '',
      emergency_medical_contact_name: profile.emergency_medical_contact_name || '',
      emergency_medical_contact_phone: profile.emergency_medical_contact_phone || '',
      notes: profile.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.student_id) {
      toast.error('Please select a student');
      return;
    }
    setSubmitting(true);
    try {
      if (editingId) {
        const { student_id, ...rest } = formData;
        await healthService.updateProfile(editingId, rest);
        toast.success('Health profile updated');
      } else {
        await healthService.createProfile(formData);
        toast.success('Health profile created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save health profile');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await healthService.deleteProfile(deleteTarget);
      toast.success('Health profile deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete health profile');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading health profiles...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Health — Student Profiles</h1>
            <p>Blood group, allergies, chronic conditions, and emergency medical contact</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Add Health Profile
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by student name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="table-container">
          {filteredProfiles.length === 0 ? (
            <div className="empty-state">
              <p>No health profiles found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Blood Group</th>
                  <th>Allergies</th>
                  <th>Chronic Conditions</th>
                  <th>Emergency Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProfiles.map((profile) => (
                  <tr key={profile.id}>
                    <td>{getStudentName(profile.student_id)}</td>
                    <td>{profile.blood_group || '-'}</td>
                    <td>{profile.allergies || '-'}</td>
                    <td>{profile.chronic_conditions || '-'}</td>
                    <td>
                      {profile.emergency_medical_contact_name
                        ? `${profile.emergency_medical_contact_name} (${profile.emergency_medical_contact_phone || 'no phone'})`
                        : '-'}
                    </td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(profile)}>
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => setDeleteTarget(profile.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Health Profile' : 'Add Health Profile'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {!editingId && (
                <div className="form-group">
                  <label>Student *</label>
                  <select value={formData.student_id} onChange={(e) => setFormData({ ...formData, student_id: e.target.value })} required>
                    <option value="">Select Student</option>
                    {students.map((s) => (
                      <option key={s.id} value={s.student_id}>{s.first_name} {s.last_name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Blood Group</label>
                  <input type="text" value={formData.blood_group} onChange={(e) => setFormData({ ...formData, blood_group: e.target.value })} placeholder="e.g., O+" />
                </div>
              </div>

              <div className="form-group">
                <label>Allergies</label>
                <textarea value={formData.allergies} onChange={(e) => setFormData({ ...formData, allergies: e.target.value })} placeholder="e.g., Penicillin, peanuts" />
              </div>

              <div className="form-group">
                <label>Chronic Conditions</label>
                <textarea value={formData.chronic_conditions} onChange={(e) => setFormData({ ...formData, chronic_conditions: e.target.value })} placeholder="e.g., Asthma" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Emergency Medical Contact Name</label>
                  <input type="text" value={formData.emergency_medical_contact_name} onChange={(e) => setFormData({ ...formData, emergency_medical_contact_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Emergency Contact Phone</label>
                  <input type="tel" value={formData.emergency_medical_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_medical_contact_phone: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Health Profile"
        message="This will permanently delete this health profile. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default HealthProfilesPage;
